import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {useParams, useNavigate, Link, useLocation} from 'react-router-dom';
import { observer, inject } from 'mobx-react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

import Utils, {capitalizeFirstLetter} from "../utils/utils";
import Modal from '../components/modal';
import FormSelectUser from "../components/form-select-user";
import { fetch } from '../utils/api-wrapper';
import { readApiErrorCode } from '../utils/api-error';
import FormTextfield from "../components/form-textfield";
import XpenseList from "../components/xpense-list";
import Expense from "../components/expense";
import MileageList from "../components/mileage-list";
import Tooltip from "../components/tooltip/tooltip";
import ExpensesAccountDetail from "../components/expenses-account-detail";
import { hasRequestableExceptionIssues, shouldHideRequestExceptionsButton } from "../utils/expense-account-exceptions";
import { formatValidateExpenseError } from "../utils/resolve-validation-message";
import { stopClickReachingOverlay } from '../utils/overlay-click';

const ExpensesAccountCreation = ({ data }) => {
    const source = axios.CancelToken.source();
    const { account_id } = useParams(); // Access the id parameter from the URL
    const [expensesAccount, setExpensesAccount] = useState();
    const [showRequestApproval, setShowRequestApproval] = useState(false);
    const [currentExpense, setCurrentExpense] = useState(null);
    const [isNewExpenseCreation, setIsNewExpenseCreation] = useState(false);
    const [savingError, setSavingError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [action, setAction] = useState(null);
    const [approver, setApprover] = useState(null);
    const [approvers, setApprovers] = useState([]);
    const [successMessage, setSuccessMessage] = useState(null);
    const [successMessageStyle, setSuccessMessageStyle] = useState('account');
    const timeoutId = useRef(null);
    const navigate = useNavigate(); // Hook to navigate programmatically
    const location = useLocation();
    const [showDescriptionModal, setShowDescriptionModal] = useState(false);
    const [previousStatus, setPreviousStatus] = useState(null);
    const [descriptionError, setDescriptionError] = useState(false);
    const [selectedExpenseChecksums, setSelectedExpenseChecksums] = useState([]);
    const [emailActionExpense, setEmailActionExpense] = useState(null);
    const [showEmailExceptionActionModal, setShowEmailExceptionActionModal] = useState(false);
    const [emailExceptionAction, setEmailExceptionAction] = useState(null);
    const [emailExceptionReason, setEmailExceptionReason] = useState("");
    const [hasRequestableExceptions, setHasRequestableExceptions] = useState(false);
    const formatMoney = (amount) => `$ ${Number(amount || 0).toFixed(2)}`;

    /** XPEN-2821 — swap one expense in the account list so reopen shows saved data before fetch completes. */
    const patchExpenseInAccount = (account, savedExpense) => {
        if (!account?.expenses?.length || !savedExpense?.checksum) {
            return account;
        }
        const index = account.expenses.findIndex((item) => item.checksum === savedExpense.checksum);
        if (index === -1) {
            return account;
        }
        const expenses = [...account.expenses];
        expenses[index] = savedExpense;
        return { ...account, expenses };
    };

    const isEmailExceptionApprovalFlow = () => {
        const params = new URLSearchParams(location.search);
        return params.get("module") === "expense-exception-approval";
    }

    const resolveMissingEmailActionExpenseMessage = (account) => {
        if (!account) {
            return readIssueMessage("exception_request_no_longer_available");
        }

        if (account.status === "Submitted") {
            return readIssueMessage("expense_account_already_submitted");
        }

        if (account.status !== "Pending_Exception_Approval") {
            return readIssueMessage("exception_request_already_resolved");
        }

        return readIssueMessage("exception_not_assigned_to_you");
    }

    const refreshExceptionRequestEligibility = () => {
        fetch(`/expense_account/validation/${account_id}`, "GET", {},
            response => {
                setHasRequestableExceptions(hasRequestableExceptionIssues(response?.data));
            },
            () => {
                setHasRequestableExceptions(false);
            },
            false,
            source.token);
    }

    const fetchExpensesAccount = () => {
        const searchData = `?account_id=${encodeURIComponent(account_id)}`;

        fetch(`/expenses_account/${searchData}`, "GET", {},
            (response) => {

                setExpensesAccount(response.data.expenses_account);
                refreshExceptionRequestEligibility();
                if (response.data.expenses_account.expenses.find(x => x.json?.type !== 'mileage' && ["Pending", "Processing"].includes(x.status)) !== undefined)
                    timeoutId.current = setTimeout(
                        () => fetchExpensesAccount(), 3000
                    );
                else
                    clearInterval(timeoutId.current);
            }
            ,
            (error) => {
                if (error.code === "ERR_NETWORK")
                    fetchExpensesAccount();
                else if (isEmailExceptionApprovalFlow()) {
                    setSavingError(resolveMissingEmailActionExpenseMessage(null));
                }
            }
            , false, source.token);
    }

    useEffect(() => {
        fetchExpensesAccount();
    }
        , [account_id])

    useEffect(() => {
        setPreviousStatus(expensesAccount?.status)

        if (!expensesAccount?.status || previousStatus === null || previousStatus === undefined)
            return;

        if (previousStatus !== expensesAccount.status) {
            setSuccessMessageStyle('account');
            setSuccessMessage(`${data.t.read('expenses_account')} ${data.t.read(`expenses_account_status.${expensesAccount.status.toLowerCase()}`)}`);
        }

        setPreviousStatus(expensesAccount.status)
    }
        , [expensesAccount?.status])

    useEffect(() => {
        fetchApprover();
        return () => {
            source.cancel('expenses_account_creation unmounted');
            clearInterval(timeoutId.current);
        }
    }, [])

    useEffect(() => {
        if (!expensesAccount?.expenses || !data.username) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const module = params.get("module");
        const checksum = params.get("checksum");
        const action = params.get("action");

        if (module !== "expense-exception-approval" || !checksum || !["approve", "deny"].includes(action)) {
            return;
        }

        const targetExpense = (expensesAccount.expenses || []).find(expense => expense.checksum === checksum);
        if (!targetExpense) {
            setSavingError(resolveMissingEmailActionExpenseMessage(expensesAccount));
            clearEmailApprovalQueryParams();
            return;
        }

        const approval = targetExpense.exception_approval || {};
        if (!(approval.status === "pending" && approval.approver?.username === data.username)) {
            if (approval.approver?.username && approval.approver?.username !== data.username) {
                setSavingError(readIssueMessage("exception_not_assigned_to_you"));
            } else if (approval.status === "approved") {
                setSavingError(readIssueMessage("exception_already_approved"));
            } else if (approval.status === "rejected") {
                setSavingError(readIssueMessage("exception_already_rejected"));
            } else {
                setSavingError(readIssueMessage("exception_issue_not_pending"));
            }
            clearEmailApprovalQueryParams();
            return;
        }

        setEmailActionExpense(targetExpense);
        setEmailExceptionAction(action);
        setShowEmailExceptionActionModal(true);
    }, [expensesAccount, data.username, location.search]);

    const closeInvoicePrompt = () => {
        fetchExpensesAccount();
        setCurrentExpense(null);
        setIsNewExpenseCreation(false);
    }

    const readIssueMessage = (message) => {
        if (!message) {
            return "";
        }

        try {
            const translated = data.t.read(message);
            return translated ?? message;
        } catch {
            return message;
        }
    }

    const formatUnresolvableExpenses = (unresolvableExpenses) => {
        if (!Array.isArray(unresolvableExpenses) || unresolvableExpenses.length === 0) {
            return "";
        }

        return unresolvableExpenses
            .map((expense) => {
                const expenseName = expense?.expense_name || expense?.checksum || "";
                const reason = readIssueMessage(expense?.reason);
                return expenseName && reason ? `${expenseName}: ${reason}` : (expenseName || reason);
            })
            .filter(Boolean)
            .join("\n");
    }

    const renderSavingError = () => {
        const lines = String(savingError || "")
            .split("\n")
            .map(line => line.trim())
            .filter(Boolean);

        if (lines.length <= 1) {
            return <div className="text-danger mb-0" role="alert">{savingError}</div>;
        }

        const [headline, ...details] = lines;

        return (
            <div>
                <div className="text-danger font-weight-bold mb-3">{headline}</div>
                <div className="border rounded p-3" style={{ backgroundColor: "#fff8f8" }}>
                    {details.map((detail, index) => (
                        <div
                            key={`${detail}-${index}`}
                            className={index === details.length - 1 ? "text-danger mb-0" : "text-danger mb-2 pb-2"}
                            style={index === details.length - 1 ? {} : { borderBottom: "1px solid #f5c6cb" }}
                        >
                            {detail}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const handleSubmit = () => {
        const description = (expensesAccount?.description || "").trim();
        if (!description) {
            setDescriptionError(true);
            setShowDescriptionModal(true);
        } else {
            setDescriptionError(false);
            setShowDescriptionModal(false);
            validateBeforeSubmit()
        }
    }

    const clearEmailApprovalQueryParams = () => {
        navigate(location.pathname, { replace: true });
    }

    const submit = () => {
        setSaving(true)
        setAction('submit');
        fetch("/expense_account/submit/" + account_id, "PUT", null,
            response => {
                setSaving(false);
                navigate(`/expenses_accounts/`);
            },
            error => {
                setSaving(false);
                if (error.response?.status !== 500 && readApiErrorCode(error))
                    setSavingError(`${data.t.read("internal_saving_error")} : ${readIssueMessage(readApiErrorCode(error))}`);
                else
                    setSavingError(data.t.read("internal_saving_error"));
            })
    }

    const validateBeforeSubmit = () => {
        setSaving(true);
        setAction('validate_submit');
        fetch(`/expense_account/validation/${account_id}`, "GET", {},
            response => {
                const validation = response?.data ?? {};
                const blockingIssues = validation.blocking_issues ?? [];
                const exceptionIssues = validation.exception_issues ?? [];
                const issues = [...blockingIssues, ...exceptionIssues];

                if (issues.length > 0) {
                    setSaving(false);
                    setAction(null);
                    setSavingError(
                        issues
                            .map(issue => issue.expense_name ? `${issue.expense_name}: ${readIssueMessage(issue.message)}` : readIssueMessage(issue.message))
                            .join("\n")
                    );
                    return;
                }

                submit();
            },
            error => {
                setSaving(false);
                setAction(null);
                if (error.response?.status !== 500 && readApiErrorCode(error))
                    setSavingError(`${data.t.read("validation_error")} : ${readIssueMessage(readApiErrorCode(error))}`);
                else
                    setSavingError(data.t.read("validation_error"));
            })
    }

    const updateExpenseAccount = (update) => {
        setSaving(true)
        setAction('update_expense_account');
        fetch("/expense_account/" + account_id, "PUT", update,
            response => {
                if (response?.data?.expenses_account)
                    setExpensesAccount(response?.data?.expenses_account);
                setSaving(false);
            },
            error => {
                setSaving(false);
                if (error.response?.status !== 500 && readApiErrorCode(error))
                    setSavingError(`${data.t.read("internal_saving_error")} : ${readIssueMessage(readApiErrorCode(error))}`);
                else
                    setSavingError(data.t.read("internal_saving_error"));
            })
    }

    const fetchApprover = () => {
        fetch("/client/users/approvers", "GET", {},
            response => {
                setApprovers(response.data);
                setApprover(response.data ? response.data[0] : null);
            })
    }

    const requestExceptionApproval = () => {
        setSaving(true);
        setAction('request_exception_approval');
        fetch("/expense_account/exceptions/" + account_id, "POST", { approver, checksums: selectedExpenseChecksums },
            response => {
                setSaving(false);
                setShowRequestApproval(false);
                setSelectedExpenseChecksums([]);
                fetchExpensesAccount();
            },
            error => {
                setSaving(false);
                setShowRequestApproval(false);
                if (error.response?.status !== 500 && readApiErrorCode(error)) {
                    const unresolvedSummary = formatUnresolvableExpenses(error.response?.data?.unresolvable_expenses);
                    const baseMessage = `${data.t.read("internal_saving_error")} : ${readIssueMessage(readApiErrorCode(error))}`;
                    setSavingError(unresolvedSummary ? `${baseMessage}\n${unresolvedSummary}` : baseMessage);
                }
                else
                    setSavingError(data.t.read("internal_saving_error"));
            })
    }

    const approveExceptionIssue = (expense) => {
        setSaving(true);
        setAction("approve_exception_issue");
        fetch(`/expense_account/exceptions/${account_id}/${expense.checksum}/approve`, "PUT", {},
            (response) => {
                setSaving(false);
                setAction(null);
                setShowEmailExceptionActionModal(false);
                setEmailActionExpense(null);
                setEmailExceptionAction(null);
                setEmailExceptionReason("");
                clearEmailApprovalQueryParams();

                if (response?.data?.expenses_account?.status === "Submitted") {
                    handleExpenseAccountSubmitted(response.data.expenses_account);
                } else {
                    fetchExpensesAccount();
                }
            },
            (error) => {
                setSaving(false);
                setAction(null);
                if (error.response?.data?.message === "forbidden")
                    setSavingError(readIssueMessage("exception_not_assigned_to_you"));
                else if (error.response?.status !== 500 && readApiErrorCode(error))
                    setSavingError(`${data.t.read("internal_saving_error")} : ${readIssueMessage(readApiErrorCode(error))}`);
                else
                    setSavingError(data.t.read("internal_saving_error"));
            })
    }

    const rejectExceptionIssue = (expense, reason) => {
        setSaving(true);
        setAction("reject_exception_issue");
        fetch(`/expense_account/exceptions/${account_id}/${expense.checksum}/reject`, "PUT", { reason },
            () => {
                setSaving(false);
                setAction(null);
                setShowEmailExceptionActionModal(false);
                setEmailActionExpense(null);
                setEmailExceptionAction(null);
                setEmailExceptionReason("");
                clearEmailApprovalQueryParams();
                fetchExpensesAccount();
            },
            (error) => {
                setSaving(false);
                setAction(null);
                if (error.response?.data?.message === "forbidden")
                    setSavingError(readIssueMessage("exception_not_assigned_to_you"));
                else if (error.response?.status !== 500 && readApiErrorCode(error))
                    setSavingError(`${data.t.read("internal_saving_error")} : ${readIssueMessage(readApiErrorCode(error))}`);
                else
                    setSavingError(data.t.read("internal_saving_error"));
            })
    }

    const handleRequestExceptions = () => {
        setSaving(true);
        setAction("validate_exceptions");
        fetch(`/expense_account/validation/${account_id}`, "GET", {},
            response => {
                const validation = response?.data ?? {};
                setHasRequestableExceptions(hasRequestableExceptionIssues(validation));
                const blockingIssues = validation.blocking_issues ?? [];
                const exceptionIssues = validation.exception_issues ?? [];
                const selectedIssues = exceptionIssues.filter(issue => selectedExpenseChecksums.includes(issue.expense_checksum));

                if (blockingIssues.length > 0) {
                    setSaving(false);
                    setAction(null);
                    setSavingError(blockingIssues.map(issue => readIssueMessage(issue.message)).join("\n"));
                    return;
                }

                const alreadyApprovedExpenses = (expensesAccount?.expenses || []).filter(expense =>
                    selectedExpenseChecksums.includes(expense.checksum) &&
                    expense.exception_approval?.status === "approved"
                );

                if (alreadyApprovedExpenses.length > 0) {
                    setSaving(false);
                    setAction(null);
                    setSavingError([
                        readIssueMessage("exception_issues_already_approved"),
                        ...alreadyApprovedExpenses.map(expense => expense.json?.Description ?? expense.name ?? expense.checksum)
                    ].join("\n"));
                    return;
                }

                if (selectedExpenseChecksums.length === 0 || selectedIssues.length === 0) {
                    setSaving(false);
                    setAction(null);
                    setSavingError(readIssueMessage("no_exception_issues"));
                    return;
                }

                const alreadyRequestedExpenses = (expensesAccount?.expenses || []).filter(expense =>
                    selectedExpenseChecksums.includes(expense.checksum) &&
                    expense.exception_approval?.status === "pending"
                );

                if (alreadyRequestedExpenses.length > 0) {
                    setSaving(false);
                    setAction(null);
                    setSavingError([
                        readIssueMessage("exception_issue_already_requested"),
                        ...alreadyRequestedExpenses.map(expense => expense.json?.Description ?? expense.name ?? expense.checksum)
                    ].join("\n"));
                    return;
                }

                setSaving(false);
                setAction(null);
                setShowRequestApproval(true);
            },
            error => {
                setSaving(false);
                setAction(null);
                if (error.response?.status !== 500 && readApiErrorCode(error))
                    setSavingError(`${data.t.read("validation_error")} : ${readIssueMessage(readApiErrorCode(error))}`);
                else
                    setSavingError(data.t.read("validation_error"));
            })
    }

    const disApproveExpense = (expense, causeDenial) => {
        expense.status = 'Denied';
        expense.status_details = new Date().toLocaleString() + ' : ' + data.firstName + ' ' + data.lastName + ' : ' + causeDenial + '\n' + expense?.status_details;
        return updateExpense(expense);
    }

    const approveExpense = (expense) => {
        expense.status = 'Approved';
        return updateExpense(expense);
    }

    const validateExpense = (expense) => {
        setAction('validating');
        // XPEN-2658: normalize legacy null-typed receipts before PUT — API rejects null type.
        const payload = { ...expense, json: { ...expense.json, type: expense.json?.type ?? 'receipt' } };
        // Return a Promise to ensure we have a resolved value
        return new Promise((resolve) => {
            fetch(`/expenses/validate/${account_id}/${expense.checksum}`,
                "PUT",
                payload,
                response => {
                    resolve(true); // Resolve with true on success
                },
                error => {
                    setSavingError(formatValidateExpenseError(data.t, error?.response?.data?.message));
                    resolve(false); // Resolve with false on error
                }
            );
        });
    }

    const updateExpense = (expense, action = 'update_approval') => {
        setAction(action);
        return new Promise((resolve) => {
            fetch("/expenses/" + account_id + "/" + expense.checksum, "PUT", expense,
                response => {
                    setAction(null);
                    resolve(true);
                },
                error => {
                    setAction(null);
                    resolve(false);
                    setSavingError(data.t.read("internal_saving_error"))
                })
        });
    }

    const handleExpenseAccountSubmitted = (submittedAccount) => {
        if (submittedAccount) {
            setExpensesAccount(submittedAccount);
            setPreviousStatus(submittedAccount.status);
        }
        setSuccessMessageStyle('account');
        setSuccessMessage('expense account submitted');
    }

    const submittedTotals = expensesAccount?.submitted_totals;
    const showSubmittedAndAdjustedTotals = !!submittedTotals && expensesAccount?.status !== "Pending";
    const isExpenseAccountOwner = expensesAccount?.user?.id_user === data.id_user;
    const canRequestExceptions = isExpenseAccountOwner
        && ["Pending", "Pending_Exception_Approval"].includes(expensesAccount?.status);
    const hideRequestExceptionsButton = shouldHideRequestExceptionsButton({
        isExpenseAccountOwner,
        canRequestExceptions,
        hasRequestableExceptions,
    });
    // XPEN-3458: the api decides whether this account can still be submitted and
    // hands back the i18n key for why not, so the button and the endpoint can
    // never disagree. Status alone is not enough here — a Denied account whose
    // invoice is still open is blocked too, and only the api can see that.
    const submitBlockedReason = readIssueMessage(expensesAccount?.submission_block);

    return <div className="card p-1 mt-2 rounded-lg">
        <div className="card-body">
            { !data.isMobile && (
                <Select
                    name="user"
                    value={{value: expensesAccount?.user ?? "", label: `${expensesAccount?.user?.first_name} ${expensesAccount?.user?.last_name}`}}
                    styles={Utils.styleSelect}
                    classNamePrefix="select"
                    className={data.isMobile ? 'd-flex mb-1' : 'col-3'}
                /> 
            )}
            <div className="card-title d-flex justify-content-center">
                <h2>{data.t.read("expenses_account")}</h2>
            </div>
            { !data.isMobile &&
                <div className="d-flex justify-content-center mb-2">
                    <Link hidden={expensesAccount?.status !== "Pending"} to={`/expenses_account/${account_id}/add`} className="btn btn-sm btn-primary btn-primary-shadow pb-2 pt-2">
                        {data.t.read("add_new_expense")}
                    </Link>
                </div> 
            }

            <Accordion className={"rounded-lg shadow-none border mb-3"}
                       sx={{
                           '&:before': {
                               display: 'none',
                           },
                       }}
                       disableGutters
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="information-panel-content"
                    id="information-panel-header"
                    className={"bg-light rounded-lg"}
                    
                >
                    <h3>{data.t.read("summary")}</h3>
                </AccordionSummary>
                <AccordionDetails>
                        <ExpensesAccountDetail data={data} label="Description" classes={"h4"} isDescription={true}>
                            <FormTextfield locked={saving} format="textarea" value={expensesAccount?.description} validateChange={val => updateExpenseAccount({ 'description': val })} highlight={!expensesAccount?.description}
                                           classes={"expenses_description_input"} />
                        </ExpensesAccountDetail>
                    <ExpensesAccountDetail data={data} label={data.t.read("statusTitle")} value={data.t.read(`expenses_account_status.${expensesAccount?.status.toLowerCase()}`)} />
                    <ExpensesAccountDetail data={data} label={data.t.read("invoice.currency")} value={expensesAccount?.currency} />
                    <ExpensesAccountDetail data={data} label={data.t.read("number_of_receipts")} value={expensesAccount?.expenses?.length} />
                    <ExpensesAccountDetail data={data} label={`${data.t.read("expenses_account")} ${data.t.read("invoice.sub_total")}`} value={formatMoney(expensesAccount?.Total)} />
                    <ExpensesAccountDetail data={data} label={`${data.t.read("invoice.tax")}1`} value={`$ ${expensesAccount?.Tax1?.toFixed(2)}`} />
                    <ExpensesAccountDetail data={data} label={`${data.t.read("invoice.tax")}2`} value={`$ ${expensesAccount?.Tax2?.toFixed(2)}`} />
                    <ExpensesAccountDetail data={data} label={`${data.t.read("expenses_account")} ${data.t.read("taxes")}`} value={`$ ${expensesAccount?.Tax?.toFixed(2)}`} />
                    <ExpensesAccountDetail data={data} label={`${data.t.read("tips")}`} value={`$ ${expensesAccount?.Tip?.toFixed(2)}`} />
                    {showSubmittedAndAdjustedTotals ? (
                        <>
                            <ExpensesAccountDetail data={data} label={data.t.read("expenses_account_totals.total_submitted")} value={formatMoney(submittedTotals?.BigTotalPayed)} />
                            <ExpensesAccountDetail data={data} label={data.t.read("expenses_account_totals.total_adjusted")} value={formatMoney(expensesAccount?.BigTotalPayed)} />
                        </>
                    ) : (
                        <ExpensesAccountDetail data={data} label={`${data.t.read("expenses_account")} ${data.t.read("invoice.total")}`} value={formatMoney(expensesAccount?.BigTotalPayed)} />
                    )}
                </AccordionDetails>
            </Accordion>
            <XpenseList expensesAccount={expensesAccount} fetchExpensesAccount={fetchExpensesAccount} saving={saving} action={action} approveExpense={approveExpense} disApproveExpense={disApproveExpense} currentExpense={currentExpense} setCurrentExpense={setCurrentExpense} setIsNewExpenseCreation={setIsNewExpenseCreation} validateExpense={validateExpense} selectedExpenseChecksums={selectedExpenseChecksums} onSelectedExpenseChecksumsChange={setSelectedExpenseChecksums} onRequestExceptions={handleRequestExceptions} disableRequestExceptionsButton={saving || !canRequestExceptions} hideRequestExceptionsButton={hideRequestExceptionsButton} onExpenseAccountSubmitted={handleExpenseAccountSubmitted} />
            <MileageList setSaving={setSaving} expensesAccount={expensesAccount} fetchExpensesAccount={fetchExpensesAccount} saving={saving} setCurrentExpense={setCurrentExpense} setIsNewExpenseCreation={setIsNewExpenseCreation} approveExpense={approveExpense} disApproveExpense={disApproveExpense} validateExpense={validateExpense} />
            <div className="d-flex justify-content-end m-1">
                {/* The span is required, not decorative: a disabled button emits no
                    mouse events, so a tooltip attached straight to it never opens. */}
                <Tooltip message={submitBlockedReason}>
                    <span className="d-inline-block">
                        <button type="button" className="btn btn-primary btn-primary-shadow"
                                data-testid="submit-expense-account"
                                disabled={saving || !!submitBlockedReason}
                                onClick={() => handleSubmit()}>
                            <div className="action-buttons action-buttons-sm">
                                {data.t.read("submit")}
                                {saving && action === 'submit' && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                            </div>
                        </button>
                    </span>
                </Tooltip>

            </div>
        </div>
        {
            savingError &&
            <Modal title={data.t.read("error")} okAction={() => setSavingError(null)} okText={"ok"}>
                {renderSavingError()}
            </Modal>

        }
        {
            successMessage &&
            <Modal title={successMessageStyle === 'mileage' ? data.t.read('attention') : 'Success'} okText={"OK"} okAction={() => {
                setSuccessMessage(null);
                if (expensesAccount?.status === "Submitted") {
                    navigate(`/expenses_accounts/`);
                }
            }}>
                {successMessageStyle === 'account'
                    ? <p className='alert-success'>{successMessage}</p>
                    : successMessage}
            </Modal>
        }
        {
            showEmailExceptionActionModal && emailActionExpense &&
            <Modal
                title={data.t.read(emailExceptionAction === "approve" ? "approve_exception" : "deny_exception")}
                cancelAction={() => {
                    setShowEmailExceptionActionModal(false);
                    setEmailActionExpense(null);
                    setEmailExceptionAction(null);
                    setEmailExceptionReason("");
                    clearEmailApprovalQueryParams();
                }}
                okAction={() => {
                    if (emailExceptionAction === "approve") {
                        approveExceptionIssue(emailActionExpense);
                        return;
                    }
                    rejectExceptionIssue(emailActionExpense, emailExceptionReason);
                }}
                okText={data.t.read(emailExceptionAction === "approve" ? "approve" : "reject")}
                cancelText={data.t.read("cancel")}
                invalid={emailExceptionAction === "deny" && !emailExceptionReason.trim()}
            >
                <p>
                    {(emailExceptionAction === "approve"
                        ? data.t.read("approve_exception_confirmation")
                        : data.t.read("deny_exception_confirmation")
                    ).replace("{expense}", emailActionExpense.json?.Description ?? emailActionExpense.name ?? emailActionExpense.checksum)}
                </p>
                {emailExceptionAction === "deny" && (
                    <input
                        className='form-control'
                        type="text"
                        value={emailExceptionReason}
                        onChange={e => setEmailExceptionReason(e.target.value)}
                    />
                )}
            </Modal>
        }
        {
            showRequestApproval &&
            <Modal overflow="visible" invalid={approver === null} title={data.t.read("request_exception")} cancelAction={() => setShowRequestApproval(false)} okAction={() => requestExceptionApproval()} okText={data.t.read("submit")} cancelText={data.t.read("cancel")}>
                <FormSelectUser data={data} chosenOption={approver} options={approvers.sort((a, b) => a["first_name"] + a["last_name"] > b["first_name"] + b["last_name"] ? 1 : -1)} optionChanged={option => { setApprover(option) }} />
            </Modal>
        }
        {currentExpense && <div className="expense-overlay overlay" onClick={() => closeInvoicePrompt(false)}>
            <div className="overlay-content">
                <div className="invoice-container" onClick={stopClickReachingOverlay}>
                    <Expense
                        account_status={expensesAccount.status}
                        account_submitted_at={expensesAccount.submitted_at}
                        account_id={account_id}
                        selected={currentExpense}
                        data={data}
                        isNewCreation={isNewExpenseCreation}
                        refreshExpensesAccountList={fetchExpensesAccount}
                        close={(opts) => {
                            setCurrentExpense(null);
                            setIsNewExpenseCreation(false);
                            if (opts?.savedExpense) {
                                setExpensesAccount((prev) => patchExpenseInAccount(prev, opts.savedExpense));
                            }
                            fetchExpensesAccount();
                            if (opts?.mileageSaved) {
                                setSuccessMessageStyle('mileage');
                                setSuccessMessage(data.t.read('mileage_save_success'));
                            }
                        }}
                    />
                </div>
            </div>
        </div>
        }
        {
            showDescriptionModal &&
            <form method="post" onSubmit={(e)=>{
                e.preventDefault();
                handleSubmit();
            }}>
            <Modal title={data.t.read("description")} width={data.isMobile ? "96vw" : 600} height={400} 
                   centered
                   cancelAction={() => {
                       setDescriptionError(false);
                       setShowDescriptionModal(false);
                       updateExpenseAccount({'description': ''});
                   }}
                   okIsSubmit={true}
                   okText={data.t.read("submit")}
                   cancelText={data.t.read("cancel")}
            >
                    <FormTextfield
                        locked={saving}
                        placeholder={data.t.read("description")}
                        format="textarea"
                        value={expensesAccount?.description}
                        validateChange={val => {
                            if ((val || "").trim()) setDescriptionError(false);
                            updateExpenseAccount({'description': val});
                        }}
                        classes={"expenses_description_input border p-2 min-height-150"}
                        required
                    />
                    {descriptionError && (
                        <div className="text-danger mt-2">
                            {capitalizeFirstLetter(data.t.read("description_missing"))}
                        </div>
                    )}
            </Modal>
            </form>
        }
    </div>
}
export default inject('data')(observer(ExpensesAccountCreation));
